import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import { useAccountingData } from './hooks/useAccountingData';
import AccountingOverview from './components/AccountingOverview';
import MonthlyStatementTable from './components/MonthlyStatement';
import TransactionList from './components/TransactionList';
import OrderNetting from './components/OrderNetting';
import { exportMonthlyStatementXlsx, exportTransactionsXlsx } from './utils/exportAccounting';
import { usePaywallGate } from '../../utils/paywall';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';

const PM_COLORS = ['#0d9488', '#06b6d4', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b'];

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN')}`;
}

function PaymentMethodTooltip({ active, payload }: { active?: boolean; payload?: { payload: { name: string; value: number; amount: number } }[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{d.name}</p>
      <p className="text-gray-600">{d.value} 单 · {formatAmount(d.amount)}</p>
    </div>
  );
}

export default function AccountingPage() {
  const now = new Date();
  // 默认区间用本地日历日：toISOString() 取的是 UTC 日，东八区 00:00–08:00 打开页面会少一天
  const defaultFrom = `${now.getFullYear()}-01-01`;
  // 终点收到年末而不是今天：窗口比的是预约日期，收到今天会把本年剩下的档期挡在对账之外
  const defaultTo = `${now.getFullYear()}-12-31`;

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [appliedFrom, setAppliedFrom] = useState(defaultFrom);
  const [appliedTo, setAppliedTo] = useState(defaultTo);

  const { data, loading, error, refresh } = useAccountingData(appliedFrom, appliedTo);

  const gate = usePaywallGate();
  // 墙判在点击这一刻而不是禁用按钮：会员状态是异步拉的，加载完成前据此判会恒为假，
  // 若用它锁住入口，尊享用户进页面那一瞬也会看到「不可用」的导出。
  const exportAllowed = async (): Promise<boolean> => gate('exportCsv', '导出 Excel');

  const handleApply = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };

  const pmData = data?.overview.paymentMethods ?? [];
  const pmTotal = pmData.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 pb-28 md:pb-10 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">核算管理</h1>
            <p className="text-sm text-gray-400 mt-0.5">收支核算、利润分析与财务报表</p>
          </div>
          {/* Date filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-calendar-line text-xs" />
              </div>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="text-xs text-gray-600 outline-none bg-transparent cursor-pointer"
              />
              <span className="text-gray-300 text-xs">~</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="text-xs text-gray-600 outline-none bg-transparent cursor-pointer"
              />
            </div>
            <button
              onClick={handleApply}
              className="flex items-center gap-1.5 px-3 py-2 text-xs bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-filter-3-line text-xs" />
              </div>
              筛选
            </button>
            <button
              onClick={() => refresh()}
              className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line text-xs" />
              </div>
              刷新
            </button>
            <button
              onClick={async () => {
                if (!data || !(await exportAllowed())) return;
                exportMonthlyStatementXlsx(data.monthly, `${appliedFrom}_${appliedTo}`);
              }}
              disabled={!data || loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              title="导出月度报表为 Excel 表格"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-excel-2-line text-xs" />
              </div>
              导出月报
            </button>
            <button
              onClick={async () => {
                if (!data || !(await exportAllowed())) return;
                exportTransactionsXlsx(data.transactions, `${appliedFrom}_${appliedTo}`);
              }}
              disabled={!data || loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              title="导出收支明细为 Excel 表格"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line text-xs" />
              </div>
              导出明细
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-600 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line" /></div>
            {error}
          </div>
        )}

        {/* Overview KPIs */}
        <AccountingOverview data={data?.overview ?? null} loading={loading} />

        {/* Monthly Statement */}
        <MonthlyStatementTable data={data?.monthly ?? []} loading={loading} />

        {/* Order Netting */}
        <OrderNetting />

        {/* Transaction List + Payment Method */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <TransactionList data={data?.transactions ?? []} loading={loading} />
          </div>
          {/* Payment Method Chart */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800">支付方式分布</h3>
              <p className="text-xs text-gray-400 mt-0.5">按收款金额占比</p>
            </div>
            {loading ? (
              <div className="h-48 flex items-center justify-center">
                <div className="text-sm text-gray-400 animate-pulse">加载中...</div>
              </div>
            ) : pmData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-sm text-gray-400">暂无数据</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={pmData} cx="50%" cy="50%" innerRadius={46} outerRadius={68} dataKey="amount" paddingAngle={2}>
                      {pmData.map((_, i) => (
                        <Cell key={i} fill={PM_COLORS[i % PM_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PaymentMethodTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-3">
                  {pmData.map((pm, i) => (
                    <div key={pm.name} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PM_COLORS[i % PM_COLORS.length] }} />
                      <span className="text-xs text-gray-600 flex-1 truncate">{pm.name}</span>
                      <span className="text-xs font-medium text-gray-800">{pmTotal > 0 ? Math.round((pm.amount / pmTotal) * 100) : 0}%</span>
                      <span className="text-xs text-gray-400">{pm.value}单</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
